// import SquareIcon from "@mui/icons-material/Square";
import { useMemo } from "react";
import { useDebounce } from "../hooks/use-debounce";
import { UseProvidersListProps } from "../queries/provider/setting/use-providers-list";
import { useProviderListV2 } from "../queries/use-provider-list-v2";
import { formatDate } from "../utils/date-time-utility";
import { DEF_PAGINATION, getXtenantId } from "../utils/utils";
import AsyncSelect, { AsyncSelectOption } from "./AsyncSelect";
import Flex from "./Flex";
import Text from "./Text";

interface ProviderSelectProps {
  value: AsyncSelectOption | null;
  onChange: (value: AsyncSelectOption | null) => void;
  label?: string;
  required?: boolean;
  error?: string | boolean;
  providerSearchParam?: UseProvidersListProps;
  name?: string;
  handleBlur?: (e: any) => void;
  disabled?: boolean;
  defaultSearchKey?: string;
  prefillListOption?: AsyncSelectOption;
  viewBirthDate?: boolean;
  isMatchButtonHeight?: boolean;
}

/**
 * @param value
 * @param onChange
 * @param label
 * @param required
 * @param error
 * @param providerSearchParam // pagination, searchKey, xTENANTID, status, isReferral, state, speciality, locationUuid
 * @param defaultSearchKey // default search key
 * @param disabled
 * @returns AsyncSelect
 *
 * @description using internal provider api
 *
 *
 * @author [Rajkumar Rathod]
 */
const ProviderSelect = ({
  value,
  onChange,
  label,
  required,
  error,
  providerSearchParam,
  name,
  handleBlur,
  disabled,
  defaultSearchKey,
  prefillListOption,
  viewBirthDate = true, // to show birth date in the provider select
  isMatchButtonHeight,
}: ProviderSelectProps) => {
  const [searchKey, setSearchKey] = useDebounce<string>(defaultSearchKey || "");

  const {
    data: providersList,
    isLoading,
    isFetching,
  } = useProviderListV2({
    status: providerSearchParam?.status ?? true,
    pagination: providerSearchParam?.pagination ?? DEF_PAGINATION,
    searchString: searchKey,
    xTENANTID: providerSearchParam?.xTENANTID ?? getXtenantId(),
    ...providerSearchParam,
  });

  const options: AsyncSelectOption[] = useMemo(() => {
    const list =
      providersList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.name,
        birthDate: it?.birthDate ?? "",
        colorCode: it?.colorCode ?? "",
      })) ?? [];

    if (
      prefillListOption &&
      !list.some((opt) => opt.value === prefillListOption.value)
    ) {
      return [prefillListOption, ...list];
    }

    return list;
  }, [providersList?.data?.content, prefillListOption]);

  return (
    <AsyncSelect
      name={name}
      isMatchButtonHeight={isMatchButtonHeight}
      handleBlur={handleBlur}
      required={required}
      error={error}
      loading={isLoading || isFetching}
      renderCustomLabel={(option) => (
        <Flex gap={1} alignItems={"center"} justifyContent={"row"}>
          {/* <SquareIcon
            sx={{
              backgroundColor: option.colorCode,
              color: option.colorCode,
              borderRadius: 0.8,
            }}
          /> */}
          <Flex flexDirection={"column"}>
            <Text>{option.label}</Text>
            {viewBirthDate && (
              <Text>
                {option?.birthDate ? formatDate(option?.birthDate) : ""}
              </Text>
            )}
          </Flex>
        </Flex>
      )}
      options={options}
      label={label}
      placeholder="Search Provider"
      onSearch={setSearchKey}
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
};

export default ProviderSelect;
